import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

interface PurchasePriceContextType {
  showPurchasePrice: boolean;
  hidePurchasePrice: () => void;
  showPurchasePriceRequest: () => boolean; // returns true if password is NOT required (i.e., already showing or something)
  verifyAndShow: (password: string) => Promise<{ success: boolean; message: string }>;
  isPasswordModalOpen: boolean;
  openPasswordModal: () => void;
  closePasswordModal: () => void;
}

const STORAGE_KEY = "artpos_show_purchase_price";

const PurchasePriceContext = createContext<PurchasePriceContextType | null>(null);

export function PurchasePriceProvider({ children }: { children: ReactNode }) {
  const [showPurchasePrice, setShowPurchasePrice] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored !== null ? stored === "true" : true;
  });
  const [isPasswordModalOpen, setPasswordModalOpen] = useState(false);

  const hidePurchasePrice = useCallback(() => {
    setShowPurchasePrice(false);
    localStorage.setItem(STORAGE_KEY, "false");
  }, []);

  const openPasswordModal = useCallback(() => {
    setPasswordModalOpen(true);
  }, []);

  const closePasswordModal = useCallback(() => {
    setPasswordModalOpen(false);
  }, []);

  const showPurchasePriceRequest = useCallback((): boolean => {
    if (showPurchasePrice) return false;
    setPasswordModalOpen(true);
    return false;
  }, [showPurchasePrice]);

  const verifyAndShow = useCallback(async (password: string): Promise<{ success: boolean; message: string }> => {
    try {
      const stored = sessionStorage.getItem("artpos_user");
      if (!stored) {
        return { success: false, message: "Kullanıcı bilgisi bulunamadı." };
      }

      const user = JSON.parse(stored);

      const body: Record<string, string> = {
        loginType: user.loginType || "dealer",
        dealerCode: user.dealerCode,
        password,
      };

      if (user.loginType === "dealer") {
        body.dealerName = user.dealerName;
      } else {
        body.username = user.username;
      }

      const AUTH_FUNCTION_URL = "https://dimwpgeuqvtzlrlvveme.supabase.co/functions/v1/dealer-auth";
      const response = await fetch(AUTH_FUNCTION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.success) {
        setShowPurchasePrice(true);
        localStorage.setItem(STORAGE_KEY, "true");
        setPasswordModalOpen(false);
        return { success: true, message: "Alış fiyatları gösteriliyor." };
      }

      return { success: false, message: data.message || "Şifre yanlış." };
    } catch {
      return { success: false, message: "Bağlantı hatası. Lütfen tekrar deneyin." };
    }
  }, []);

  return (
    <PurchasePriceContext.Provider
      value={{
        showPurchasePrice,
        hidePurchasePrice,
        showPurchasePriceRequest,
        verifyAndShow,
        isPasswordModalOpen,
        openPasswordModal,
        closePasswordModal,
      }}
    >
      {children}
    </PurchasePriceContext.Provider>
  );
}

export function usePurchasePrice() {
  const context = useContext(PurchasePriceContext);
  if (!context) {
    throw new Error("usePurchasePrice must be used within a PurchasePriceProvider");
  }
  return context;
}